export const CANVAS_SEARCH_PARAM = 'canvas';

export type CanvasO11yResource = {
  type: string;
  [key: string]: unknown;
};

export type CanvasView =
  | { kind: 'empty' }
  | { kind: 'o11y-resource'; resource: CanvasO11yResource }
  | { kind: 'app-route'; pattern: string; params: Record<string, string>; search?: string };

export type CanvasState = {
  revision: number;
  updatedBy: 'user' | 'agent';
  active: CanvasView;
};

export type CanvasSession = {
  view: CanvasView;
  humanLock: boolean;
  lastSeenServerRevision: number;
};

export const EMPTY_CANVAS_VIEW: CanvasView = { kind: 'empty' };

const EMPTY_SESSION: CanvasSession = {
  view: EMPTY_CANVAS_VIEW,
  humanLock: false,
  lastSeenServerRevision: -1,
};

export function canvasViewKey(view: CanvasView): string {
  return JSON.stringify(view);
}

export function parseCanvasDocument(value: unknown): CanvasState | null {
  const parsed = typeof value === 'string' ? parseJson(value) : value;
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }
  const record = parsed as Record<string, unknown>;
  const revision = record.revision;
  const updatedBy = record.updatedBy ?? record.updated_by;
  if (typeof revision !== 'number' || !Number.isFinite(revision)) {
    return null;
  }
  if (updatedBy !== 'user' && updatedBy !== 'agent') {
    return null;
  }
  const active = parseCanvasView(record.active);
  if (!active) {
    return null;
  }
  return { revision, updatedBy, active };
}

export function encodeCanvasParam(state: CanvasState): string {
  const bytes = new TextEncoder().encode(JSON.stringify(state));
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

export function decodeCanvasParam(param: string | null | undefined): CanvasState | null {
  if (!param) {
    return null;
  }
  try {
    const binary = atob(param);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return parseCanvasDocument(JSON.parse(new TextDecoder().decode(bytes)));
  } catch {
    return null;
  }
}

export function initialCanvasSession(urlCanvas: CanvasState | null): CanvasSession {
  if (!urlCanvas) {
    return EMPTY_SESSION;
  }
  return {
    view: urlCanvas.active,
    humanLock: urlCanvas.updatedBy === 'user',
    lastSeenServerRevision: urlCanvas.revision,
  };
}

export function applyServerCanvas(session: CanvasSession, server: CanvasState): CanvasSession {
  if (server.revision <= session.lastSeenServerRevision) {
    return session;
  }
  const seen: CanvasSession = { ...session, lastSeenServerRevision: server.revision };
  if (session.humanLock) {
    return seen;
  }
  if (canvasViewKey(session.view) === canvasViewKey(server.active)) {
    return seen;
  }
  return { ...seen, view: server.active };
}

export function applyUserView(session: CanvasSession, view: CanvasView): CanvasSession {
  return { ...session, view, humanLock: true };
}

export function releaseHumanLock(session: CanvasSession): CanvasSession {
  if (!session.humanLock) {
    return session;
  }
  return { ...session, humanLock: false };
}

export function canvasStateFromSession(session: CanvasSession): CanvasState {
  return {
    revision: session.lastSeenServerRevision,
    updatedBy: session.humanLock ? 'user' : 'agent',
    active: session.view,
  };
}

export function canvasParamForSession(session: CanvasSession): string | null {
  if (session.view.kind === 'empty' && !session.humanLock) {
    return null;
  }
  return encodeCanvasParam(canvasStateFromSession(session));
}

export function classifyCanvasQueryError(error: unknown): 'missing' | 'subselection' | 'other' | 'none' {
  if (!error) {
    return 'none';
  }
  const message = errorText(error);
  if (/cannot query field/i.test(message) && /canvas/i.test(message)) {
    return 'missing';
  }
  if (/unknown field/i.test(message) && /canvas/i.test(message)) {
    return 'missing';
  }
  if (/must have a selection of subfields/i.test(message) || /must have sub selection/i.test(message)) {
    return 'subselection';
  }
  return 'other';
}

function parseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function parseCanvasView(value: unknown): CanvasView | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (record.kind === 'empty') {
    return EMPTY_CANVAS_VIEW;
  }
  if (record.kind === 'o11y-resource') {
    const resource = parseO11yResource(record.resource);
    if (!resource) {
      return null;
    }
    return { kind: 'o11y-resource', resource };
  }
  if (record.kind === 'app-route') {
    if (typeof record.pattern !== 'string' || !isAllowlistedPatternShape(record.pattern)) {
      return null;
    }
    const params = parseStringRecord(record.params);
    if (!params) {
      return null;
    }
    if (record.search !== undefined && typeof record.search !== 'string') {
      return null;
    }
    return {
      kind: 'app-route',
      pattern: record.pattern,
      params,
      ...(typeof record.search === 'string' ? { search: record.search } : {}),
    };
  }
  return null;
}

function parseO11yResource(value: unknown): CanvasO11yResource | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (typeof record.type !== 'string' || record.type.length === 0) {
    return null;
  }
  const resource: CanvasO11yResource = { type: record.type };
  for (const [key, entry] of Object.entries(record)) {
    resource[key] = entry;
  }
  return resource;
}

function parseStringRecord(value: unknown): Record<string, string> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const params: Record<string, string> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry !== 'string') {
      return null;
    }
    params[key] = entry;
  }
  return params;
}

function isAllowlistedPatternShape(pattern: string): boolean {
  if (!pattern.startsWith('/')) {
    return false;
  }
  if (pattern.includes('://') || pattern.startsWith('//')) {
    return false;
  }
  return true;
}

function errorText(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }
  if (!error || typeof error !== 'object') {
    return '';
  }
  const record = error as { message?: unknown; graphQLErrors?: Array<{ message?: unknown }> };
  const parts: string[] = [];
  if (typeof record.message === 'string') {
    parts.push(record.message);
  }
  for (const gqlError of record.graphQLErrors ?? []) {
    if (typeof gqlError.message === 'string') {
      parts.push(gqlError.message);
    }
  }
  return parts.join('\n');
}
